import type { OnboardingEmailTemplate } from '../OnboardingEmailModal.types';
import { documentItem, optional, questionItem } from './templateItems';

const ENGAGEMENT_DETAILS = 'Engagement Details';
const COMPANY_DOCUMENTS = 'Company Documents/Information';
const UNDISCOUNTED_TEST = 'Step 2 - Undiscounted Test';
const DISCOUNTED_TEST = 'Step 3 - Discounted Test (Fair Value)';
const DEVELOPED_TECHNOLOGY = 'Developed Technology';
const TRADE_NAME = 'Trade Names / Trademarks';
const CUSTOMER_RELATIONSHIPS = 'Customer Relationships';
const INTERNAL_USE_SOFTWARE = 'Internal Use Software';
const NON_COMPETES = 'Non-Competes';
const LEASES = 'Favorable/Unfavorable Leases';
const ORDER_BACKLOG = 'Order Backlog';
const DEFERRED_REVENUE = 'Deferred Revenue';
const ASSEMBLED_WORKFORCE = 'Assembled Workforce';
const OTHER_INTANGIBLES = 'Other Intangible Assets';

// Based on the "ASC 360 Info" tab of the "ASC 350 and ASC 360 - Information Request List" workbook.
// Step 3 and the intangible asset sections only apply if the Asset Group fails Step 2.
export const ASC_360_IMPAIRMENT_TEMPLATE: OnboardingEmailTemplate = {
  id: 'asc-360-impairment',
  label: 'Intangible Asset Impairment (ASC 360)',
  emailSubject: 'Information request for your long-lived asset impairment analysis',
  valuationTypes: ['Intangible Asset Impairment (ASC 360)'],
  requestValuationType: 'asc_360_impairment',
  introduction: `Hello {{CONTACT_FIRST_NAME}},

Thank you for partnering with us. We are ready to move forward with the ASC 360 long-lived asset impairment analysis.

To help us build an accurate model and keep things moving efficiently, please reply to this email with your responses to the questions below and upload the requested documents to the portal. Where available, Excel versions are strongly preferred to ensure precision in our analysis.`,
  documents: [
    questionItem(ENGAGEMENT_DETAILS, 'Company name', 'Company Name'),
    questionItem(ENGAGEMENT_DETAILS, 'Asset group', 'Asset Group'),
    questionItem(ENGAGEMENT_DETAILS, 'Measurement date', 'Measurement Date'),
    questionItem(ENGAGEMENT_DETAILS, 'Auditor', 'Auditor'),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Triggering event',
      'Triggering Event - Please describe any issues that could result in long-lived asset impairment for this Asset Group'
    ),

    documentItem(
      COMPANY_DOCUMENTS,
      'Business plan / investment presentation',
      'Business plan or investment presentation for each Reporting Unit/Entity'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Public comparables',
      'A list of public competitors and public comparable firms for each Reporting Unit/Entity'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - Historical, last 2-3 years',
      'Consolidated historical income statements, balance sheets, and cash flow statements for each Reporting Unit/Entity',
      { description: 'for the past two to three fiscal years; preferably in Excel' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - LTM',
      'Last twelve-month (LTM) income statement, balance sheet, and cash flow statement for each Reporting Unit/Entity',
      {
        description:
          'as of the Measurement Date, with all amounts allocated from Corporate (both income statement and balance sheet); preferably in Excel',
      }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Balance sheet items not assumed',
      'In a hypothetical sale of the Reporting Unit/Entity, would any balance sheet items not be assumed by a willing buyer?',
      { description: 'such as intercompany accounts or related party loans, as applicable' }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Book value as proxy for fair value?',
      'Confirm book value is a reasonable proxy for fair value for all asset and liability line items on the LTM balance sheet',
      { description: 'if not, please identify those line items' }
    ),

    questionItem(
      UNDISCOUNTED_TEST,
      'Asset Group = Reporting Unit?',
      'Confirm we can assume the Asset Group is the same as the Reporting Unit/Entity for purposes of this test'
    ),
    documentItem(
      UNDISCOUNTED_TEST,
      'Asset Group projections',
      'Asset Group projections (entity-specific basis) to maintain the existing service potential',
      {
        description:
          'covering the remaining useful life of the primary long-lived asset; projected revenues plus Cost of Sales, SG&A, R&D, depreciation, amortization, and capital expenditures each year; preferably in Excel',
      }
    ),
    questionItem(
      UNDISCOUNTED_TEST,
      'Primary long-lived asset',
      'Primary long-lived asset for the Asset Group and its remaining useful life as of the Measurement Date'
    ),
    questionItem(
      UNDISCOUNTED_TEST,
      'Maintenance vs. growth capex',
      'Percentage of projected capital expenditures that represents maintenance versus expansionary/growth levels'
    ),
    documentItem(UNDISCOUNTED_TEST, 'Projected balance sheet', 'Projected balance sheet for the Asset Group', {
      description: 'if available; preferably in Excel',
    }),
    questionItem(UNDISCOUNTED_TEST, 'Tax rate', 'Tax rate for the Asset Group as of the Measurement Date'),

    questionItem(
      DISCOUNTED_TEST,
      'Intangibles to value separately',
      'Confirm the definite-lived intangible assets that will need to be separately valued',
      {
        ...optional,
        description:
          'only if the Asset Group fails Step 2; please also confirm book value is a reasonable proxy for fair value of the other long-lived assets',
      }
    ),

    questionItem(DEVELOPED_TECHNOLOGY, 'Description', 'Developed Technology - Description of asset', optional),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Multiple technologies?',
      'Are there multiple Developed Technologies with different levels of profitability and/or remaining economic useful lives?',
      { ...optional, description: 'if so, we will have some follow-up questions' }
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Annual obsolescence rate',
      'Developed Technology - Annual Obsolescence Rate',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Estimated useful life',
      'Developed Technology - Estimated Useful Life',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      '% R&D expansionary',
      '% of R&D Expense related to expansionary purposes (i.e., new products & new features)',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      '% R&D maintenance',
      '% of R&D Expense related to maintaining existing technology',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Technology licensed?',
      'Has the technology been licensed? If so, at what rate?',
      optional
    ),

    questionItem(
      TRADE_NAME,
      'List of trade names',
      'List of Trade Names/Trademarks of the Company, including Domain Names',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Remaining life & planned use',
      'Remaining economic useful life of the Trade Name and its planned use (i.e., sunset over a defined time period or continue to use)',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Time to transition',
      'Expected time to transition if a transition for the Trade Name is planned',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Trade name licensed?',
      'Has the Trade Name been licensed? If so, at what rate?',
      optional
    ),
    questionItem(
      TRADE_NAME,
      '% revenue from trade names',
      '% of projected revenues for the Asset Group attributable to the Trade Names',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Name recognition',
      'Name recognition of the Trade Names as of the Measurement Date, including brand recognition within the industry',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Trade name projection risk',
      'Riskiness of Management projections associated with achieving the projections for the Trade Name',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Trade name other risks',
      'Any other risk factors we need to consider related to the Trade Name',
      optional
    ),

    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Relationship type',
      'Are the customer relationships contractual, recurring, transactional, etc.?',
      optional
    ),
    documentItem(
      CUSTOMER_RELATIONSHIPS,
      'Customer revenue & cost projections',
      'Customer Relationships - Projected revenue and costs by Revenue, Cost of Sales, S&M, G&A, and R&D',
      {
        ...optional,
        description:
          'by customer type when types have different levels of profitability and/or remaining economic useful lives',
      }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Lost major customers?',
      'Has the Reporting Unit lost any major customers since the initial acquisition and/or last impairment analysis?',
      optional
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      '% revenue from existing customers',
      'What % of total revenue for the first and second fiscal years of the projections is attributable to each existing type of Customer Relationship?',
      {
        ...optional,
        description:
          'what are the expectations going forward? Please also provide revenue growth attributable to each type over the forecast',
      }
    ),
    documentItem(
      CUSTOMER_RELATIONSHIPS,
      'Historical attrition analysis',
      'Customer Relationships - Historical attrition analysis or revenue data by customer type',
      {
        ...optional,
        description:
          '3-5 years for the Asset Group; what has attrition been historically and what are expectations going forward?',
      }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'S&M split',
      'What % of total S&M expenses over the forecast is attributable to maintaining existing Customer Relationships versus acquiring new customers?',
      optional
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Customer projection risk',
      'Riskiness of Management projections associated with achieving the projections for Customer Relationships',
      optional
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Customer other risks',
      'Any other risk factors we need to consider related to Customer Relationships',
      optional
    ),

    questionItem(INTERNAL_USE_SOFTWARE, 'Description', 'Internal Use Software - Description of asset', optional),
    questionItem(
      INTERNAL_USE_SOFTWARE,
      'Sold to customers?',
      'Is the internal use software sold to customers/clients generating revenue?',
      optional
    ),
    questionItem(
      INTERNAL_USE_SOFTWARE,
      'Sold separately?',
      'Could the internal use software be sold separate to the existing technology offering?',
      optional
    ),
    documentItem(INTERNAL_USE_SOFTWARE, 'Internal Use Software template', 'Completed Internal Use Software template', {
      ...optional,
      description: 'estimated total investment and time period (in years) to recreate the software from scratch',
    }),

    questionItem(
      NON_COMPETES,
      'Covenantors & term',
      'With whom does the Non-Compete apply, and what is the term of the agreement?',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Probability to compete',
      'Probability that the covenantor competes if a non-compete was not in place',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Revenue impact',
      'Impact to revenue if the covenantor competes over the term of the agreement (as a % of revenue)',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Covenantor equity/earnout?',
      'Did any covenantors receive equity as part of the original transaction or are eligible for contingent consideration?',
      optional
    ),

    documentItem(
      LEASES,
      'Off-market lease agreements',
      'Lease agreements not expected to be at market rates (i.e., favorable/unfavorable)',
      optional
    ),
    questionItem(LEASES, 'Contract details', 'Lease contract details', optional),
    questionItem(LEASES, 'Company economic rate/IBR', 'Current Economic Rate/IBR of the Company', optional),

    questionItem(ORDER_BACKLOG, 'Backlog amount', 'Total amount of Order Backlog as of the Measurement Date', optional),
    questionItem(
      ORDER_BACKLOG,
      'Backlog fulfillment period',
      'Projected annual fulfillment period of backlog (i.e., waterfall)',
      optional
    ),
    questionItem(ORDER_BACKLOG, 'Probability of fulfillment', 'Probability of backlog fulfillment', optional),

    questionItem(
      DEFERRED_REVENUE,
      'Deferred revenue amount',
      'Total deferred revenue assumed as of the Measurement Date',
      optional
    ),
    questionItem(
      DEFERRED_REVENUE,
      'Deferred revenue fulfillment period',
      'Projected annual fulfillment period of deferred revenue (i.e., waterfall)',
      optional
    ),

    documentItem(ASSEMBLED_WORKFORCE, 'Assembled Workforce template', 'Completed Assembled Workforce template', {
      ...optional,
      description:
        'employees in place at the Asset Group as of the Measurement Date, reflective of market participant assumptions',
    }),

    questionItem(
      OTHER_INTANGIBLES,
      'Other intangible assets',
      'Any other Intangible Assets not stated above that may require a separate impairment analysis?',
      { ...optional, description: 'please include a description' }
    ),
  ],
};
